import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import AdminCompanyBilling from "./AdminCompanyBilling";

const WEEKDAYS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];
const ROLE_LABELS = { admin: "Admin", beheer: "Beheer", kijken: "Kijken" };

// Bedrijfsprofiel: gegevens, logo, websites, projecten, status en team access.
export default function AdminCompanyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [members, setMembers] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState({ email: "", role: "beheer" });
  const [proj, setProj] = useState(null); // bewerk-formulier project
  const fileRef = useRef(null);

  const load = () => {
    api.get(`/companies/${id}`).then(({ data }) => {
      setData(data);
      setForm({
        name: data.company.name, official_website: data.company.official_website || "",
        contact_name: data.company.contact_name || "", contact_email: data.company.contact_email || "",
        contact_phone: data.company.contact_phone || "", notes: data.company.notes || "",
      });
    }).catch((err) => {
      const s = err?.response?.status;
      if (s === 401) { localStorage.removeItem("joh_admin_token"); nav("/admin/login?next=/admin/bedrijven", { replace: true }); }
      else if (s === 403 || s === 404) nav("/admin/bedrijven", { replace: true });
      else toast.error("Laden mislukt");
    });
    api.get(`/companies/${id}/members`).then(({ data }) => setMembers(data.items)).catch(() => setMembers([]));
  };
  useEffect(load, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !form) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><span className="chip text-[#4A4A4A]" data-testid="company-loading">BEDRIJF …</span></div>;
  const c = data.company;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/companies/${id}`, form);
      toast.success("Opgeslagen");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Opslaan mislukt"); }
    setBusy(false);
  };

  const setStatus = async (action) => {
    try {
      await api.post(`/companies/${id}/status`, { action });
      toast.success("Status bijgewerkt");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: up } = await api.post("/admin/media/upload", fd);
      await api.patch(`/companies/${id}`, { logo_url: up.url });
      toast.success("Logo opgeslagen");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload mislukt"); }
  };

  const sendInvite = async () => {
    if (!invite.email.trim()) { toast.error("E-mailadres is verplicht"); return; }
    try {
      await api.post(`/companies/${id}/team/invite`, invite);
      toast.success("Uitnodiging verstuurd");
      setInvite({ email: "", role: "beheer" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Uitnodigen mislukt"); }
  };

  const teamAction = async (mid, action, body) => {
    try {
      await api.post(`/companies/${id}/team/${mid}/${action}`, body || {});
      toast.success("Bijgewerkt");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  const saveProject = async () => {
    try {
      if (proj.id) await api.patch(`/companies/${id}/projects/${proj.id}`, proj);
      else await api.post(`/companies/${id}/projects`, proj);
      toast.success("Project opgeslagen");
      setProj(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); }
  };

  const team = data.team.filter((t) => t.status === "active");
  const revoked = data.team.filter((t) => t.status === "revoked");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F5F3F0] px-5 md:px-10 py-8" data-testid="admin-company-detail">
      <Link to="/admin/bedrijven" className="chip text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-2 transition-colors" data-testid="company-back"><ArrowLeft size={12} /> BEDRIJVEN</Link>

      <div className="flex items-start justify-between gap-6 mt-4 flex-wrap">
        <div className="flex items-center gap-4">
          {c.logo_url
            ? <img src={c.logo_url} alt="" className="w-14 h-14 object-contain" data-testid="company-logo" />
            : <div className="w-14 h-14 border border-[#F5F3F0]/20 flex items-center justify-center chip text-[#4A4A4A]">{c.name.slice(0, 2)}</div>}
          <div>
            <h1 className="font-display text-3xl uppercase" data-testid="company-name">{c.name}</h1>
            <div className="chip text-[#4A4A4A] mt-1" data-testid="company-status">STATUS / {c.status.toUpperCase()}</div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="company-status-actions">
          {c.status !== "active" && <button onClick={() => setStatus("activate")} className="btn-light" data-testid="company-activate">ACTIVEREN</button>}
          {c.status === "active" && <button onClick={() => setStatus("deactivate")} className="btn-ghost-dark text-[#D4A93A] border-[#D4A93A]/40" data-testid="company-deactivate">DEACTIVEREN</button>}
          {c.status !== "archived" && <button onClick={() => setStatus("archive")} className="btn-ghost-dark text-[#A3A3A3] border-[#F5F3F0]/25" data-testid="company-archive">ARCHIVEREN</button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 mt-10">
        {/* ---- Gegevens ---- */}
        <section data-testid="company-section-details">
          <div className="chip text-[#4A4A4A] mb-4">GEGEVENS</div>
          <div className="space-y-4">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="BEDRIJFSNAAM *" className="editorial-input dark w-full" data-testid="company-edit-name" />
            <input value={form.official_website} onChange={(e) => setForm({ ...form, official_website: e.target.value })} placeholder="OFFICIËLE WEBSITE (https://…)" className="editorial-input dark w-full" data-testid="company-edit-website" />
            <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="CONTACTPERSOON" className="editorial-input dark w-full" data-testid="company-edit-contact" />
            <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="E-MAIL CONTACT" className="editorial-input dark w-full" data-testid="company-edit-email" />
            <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="TELEFOON" className="editorial-input dark w-full" data-testid="company-edit-phone" />
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="INTERNE NOTITIES" className="editorial-input dark w-full" data-testid="company-edit-notes" />
          </div>
          <div className="flex items-center gap-3 mt-6 flex-wrap">
            <button onClick={save} disabled={busy} className="btn-light disabled:opacity-40" data-testid="company-save">{busy ? "…" : "OPSLAAN"}</button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="company-logo-input" />
            <button onClick={() => fileRef.current?.click()} className="btn-ghost-dark text-[#A3A3A3] border-[#F5F3F0]/25 inline-flex items-center gap-2" data-testid="company-logo-upload"><Upload size={13} /> LOGO UPLOADEN</button>
            {c.official_website && (
              <a href={c.official_website} target="_blank" rel="noreferrer" className="chip text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-1.5 transition-colors" data-testid="company-website-link">
                <ExternalLink size={11} /> {c.official_website.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        </section>

        {/* ---- Projecten ---- */}
        <section data-testid="company-section-projects">
          <div className="flex items-center justify-between mb-4">
            <div className="chip text-[#4A4A4A]">JOH PROJECTPAGINA'S</div>
            <button onClick={() => setProj({ name: "", slug: "", days: [{ weekday: 2, start_time: "18:00", end_time: "19:00" }], timezone: "Europe/Amsterdam", location: c.name === "PROVOKE" ? "JOH Boxing, Rhoneweg 38, Amsterdam Sloterdijk" : "", enabled: true })}
              className="chip text-[#D4A93A] hover:text-[#F5F3F0] inline-flex items-center gap-1.5 transition-colors" data-testid="project-add-open"><Plus size={11} /> PROJECT</button>
          </div>
          <div className="space-y-3">
            {data.projects.map((p) => (
              <button key={p.id} onClick={() => setProj({ id: p.id, name: p.name, slug: p.slug, days: p.schedule.days.map((d) => ({ ...d })), timezone: p.schedule.timezone, location: p.schedule.location, enabled: p.enabled })}
                className="w-full text-left border border-[#F5F3F0]/15 p-4 hover:border-[#D4A93A]/60 transition-colors" data-testid={`project-card-${p.slug}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-display uppercase">{p.name}</span>
                  <span className={`chip ${p.enabled ? "text-[#7BAF7B]" : "text-[#4A4A4A]"}`}>{p.enabled ? "ACTIEF" : "UIT"}</span>
                </div>
                <div className="chip text-[#4A4A4A] mt-2">johboxing.com/{p.slug}</div>
                <div className="text-sm text-[#A3A3A3] mt-2 space-y-1" data-testid={`project-days-${p.slug}`}>
                  {p.schedule.days.map((d, i) => (
                    <div key={i}>{WEEKDAYS[d.weekday]} {d.start_time}–{d.end_time}</div>
                  ))}
                </div>
                <div className="text-sm text-[#666666] mt-1">{p.schedule.location || "—"}</div>
              </button>
            ))}
            {data.projects.length === 0 && <p className="text-sm text-[#4A4A4A]">Nog geen projecten.</p>}
          </div>
        </section>
      </div>

      {/* ---- Team access ---- */}
      <section className="mt-12" data-testid="company-section-team">
        <div className="chip text-[#4A4A4A] mb-4">TEAM ACCESS</div>
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="space-y-px border border-[#F5F3F0]/10" data-testid="team-list">
              {team.map((m) => (
                <div key={m.id} className="bg-[#0A0A0A] px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`team-row-${m.email}`}>
                  <span className="text-sm flex-1 min-w-0 truncate">{m.email}</span>
                  <select value={m.role} onChange={(e) => teamAction(m.id, "role", { role: e.target.value })}
                    className="editorial-input dark !w-auto text-xs" data-testid={`team-role-${m.email}`}>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <button onClick={() => teamAction(m.id, "revoke")} className="chip text-[#D4A93A] hover:text-[#F5F3F0] transition-colors" data-testid={`team-revoke-${m.email}`}>INTREKKEN</button>
                </div>
              ))}
              {team.length === 0 && <div className="px-4 py-3 text-sm text-[#4A4A4A]" data-testid="team-empty">Nog geen teamleden.</div>}
            </div>
            {data.invites.length > 0 && (
              <div className="mt-4 space-y-px border border-[#F5F3F0]/10" data-testid="invites-list">
                {data.invites.map((i) => (
                  <div key={i.id} className="px-4 py-3 flex items-center gap-3" data-testid={`invite-row-${i.email}`}>
                    <span className="text-sm text-[#A3A3A3] flex-1 truncate">{i.email}</span>
                    <span className="chip text-[#4A4A4A]">{ROLE_LABELS[i.role]} · UITGENODIGD</span>
                    <button onClick={async () => { try { await api.post(`/companies/${id}/team/invites/${i.id}/resend`); toast.success("Opnieuw verstuurd"); } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); } }}
                      className="chip text-[#D4A93A] hover:text-[#F5F3F0] transition-colors" data-testid={`invite-resend-${i.email}`}>OPNIEUW</button>
                  </div>
                ))}
              </div>
            )}
            {revoked.length > 0 && <p className="chip text-[#4A4A4A] mt-4" data-testid="team-revoked-count">{revoked.length} INGETROKKEN</p>}
          </div>
          <div>
            <div className="chip text-[#4A4A4A] mb-3">+ NODIG TEAMLID UIT</div>
            <div className="flex gap-3 flex-wrap">
              <input value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="E-MAILADRES" className="editorial-input dark flex-1 min-w-48" data-testid="invite-email" />
              <select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} className="editorial-input dark !w-auto" data-testid="invite-role">
                {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button onClick={sendInvite} className="btn-light" data-testid="invite-submit">UITNODIGEN</button>
            </div>
            <p className="text-xs text-[#4A4A4A] mt-3 leading-relaxed">Uitnodiging verloopt na 7 dagen. Acceptatie via beveiligde link per e-mail. Rollen: Admin (volledig), Beheer (operationeel), Kijken (alleen lezen).</p>
          </div>
        </div>
      </section>

      {/* ---- Project members (goedkeuringsflow) ---- */}
      <section className="mt-12" data-testid="company-section-members">
        <div className="chip text-[#4A4A4A] mb-4">PROJECT MEMBERS</div>
        {!members && <div className="text-sm text-[#4A4A4A]">…</div>}
        {members && members.length === 0 && <p className="text-sm text-[#4A4A4A]" data-testid="members-empty">Nog geen members.</p>}
        {members && members.length > 0 && (
          <div className="space-y-px border border-[#F5F3F0]/10" data-testid="members-list">
            {members.map((m) => (
              <div key={m.id} className="bg-[#0A0A0A] px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`member-row-${m.email}`}>
                <span className="text-sm flex-1 min-w-0 truncate">{m.first_name} {m.last_name} <span className="text-[#4A4A4A]">· {m.email}</span></span>
                <span className="chip text-[#4A4A4A]">{m.project_name}</span>
                <span className={`chip ${m.status === "approved" ? "text-[#7BAF7B]" : m.status === "pending" ? "text-[#D4A93A]" : "text-[#666666]"}`} data-testid={`member-status-${m.email}`}>
                  {m.status === "approved" ? "GOEDGEKEURD" : m.status === "pending" ? "IN AFWACHTING" : "AFGEWEZEN"}
                </span>
                {m.status !== "approved" && (
                  <button onClick={async () => { try { await api.post(`/companies/${id}/members/${m.id}/approve`); toast.success("Goedgekeurd"); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); } }}
                    className="chip text-[#7BAF7B] hover:text-[#F5F3F0] transition-colors" data-testid={`member-approve-${m.email}`}>KEUR GOED</button>
                )}
                {m.status !== "rejected" && (
                  <button onClick={async () => { try { await api.post(`/companies/${id}/members/${m.id}/reject`); toast.success("Afgewezen"); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Mislukt"); } }}
                    className="chip text-[#A3A3A3] hover:text-[#F5F3F0] transition-colors" data-testid={`member-reject-${m.email}`}>WEIGER</button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---- Facturatie (centraal, alleen JOH Admin) ---- */}
      <section className="mt-12" data-testid="company-section-billing">
        <div className="chip text-[#4A4A4A] mb-4">FACTURATIE</div>
        <AdminCompanyBilling companyId={id} companyName={c.name} />
      </section>

      {/* ---- Project modal ---- */}
      {proj && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="project-modal">
          <div className="absolute inset-0 bg-black/80" onClick={() => setProj(null)} />
          <div className="relative bg-[#0A0A0A] border border-[#F5F3F0]/15 p-6 md:p-8 w-full max-w-lg">
            <h2 className="font-display text-xl uppercase" data-testid="project-modal-title">{proj.id ? "Project bewerken" : "Project toevoegen"}</h2>
            <div className="space-y-4 mt-6">
              <input value={proj.name} onChange={(e) => setProj({ ...proj, name: e.target.value })} placeholder="PROJECTNAAM *" className="editorial-input dark w-full" data-testid="project-name" />
              <input value={proj.slug} onChange={(e) => setProj({ ...proj, slug: e.target.value.toLowerCase() })} placeholder="URL-SLUG (bijv. provocative)" className="editorial-input dark w-full" data-testid="project-slug" />
              {/* trainingsdagen: meerdere dagen, elk met eigen start- en eindtijd */}
              <div data-testid="project-days">
                <div className="chip text-[#4A4A4A] mb-2">TRAININGSDAGEN</div>
                <div className="space-y-3">
                  {proj.days.map((d, i) => (
                    <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center" data-testid={`project-day-${i}`}>
                      <select value={d.weekday} onChange={(e) => setProj({ ...proj, days: proj.days.map((x, j) => j === i ? { ...x, weekday: Number(e.target.value) } : x) })}
                        className="editorial-input dark" data-testid={`project-day-${i}-weekday`}>
                        {WEEKDAYS.map((w, wi) => <option key={w} value={wi} disabled={proj.days.some((x, j) => j !== i && x.weekday === wi)}>{w}</option>)}
                      </select>
                      <input value={d.start_time} onChange={(e) => setProj({ ...proj, days: proj.days.map((x, j) => j === i ? { ...x, start_time: e.target.value } : x) })}
                        type="time" className="editorial-input dark !w-24" data-testid={`project-day-${i}-start`} />
                      <input value={d.end_time} onChange={(e) => setProj({ ...proj, days: proj.days.map((x, j) => j === i ? { ...x, end_time: e.target.value } : x) })}
                        type="time" className="editorial-input dark !w-24" data-testid={`project-day-${i}-end`} />
                      <button onClick={() => setProj({ ...proj, days: proj.days.filter((_, j) => j !== i) })} disabled={proj.days.length <= 1}
                        className="chip text-[#A3A3A3] hover:text-[#F5F3F0] disabled:opacity-30 transition-colors px-1" title="Dag verwijderen" data-testid={`project-day-${i}-remove`}>×</button>
                    </div>
                  ))}
                </div>
                <button onClick={() => {
                    const used = proj.days.map((d) => d.weekday);
                    const free = [0, 1, 2, 3, 4, 5, 6].find((w) => !used.includes(w));
                    if (free === undefined) return;
                    setProj({ ...proj, days: [...proj.days, { weekday: free, start_time: "18:00", end_time: "19:00" }] });
                  }} disabled={proj.days.length >= 7}
                  className="chip text-[#D4A93A] hover:text-[#F5F3F0] disabled:opacity-30 transition-colors mt-3 inline-flex items-center gap-1.5" data-testid="project-day-add">
                  <Plus size={11} /> DAG TOEVOEGEN
                </button>
              </div>
              <input value={proj.location} onChange={(e) => setProj({ ...proj, location: e.target.value })} placeholder="LOCATIE" className="editorial-input dark w-full" data-testid="project-location" />
              <label className="flex items-center gap-3 text-sm text-[#A3A3A3]">
                <input type="checkbox" checked={proj.enabled} onChange={(e) => setProj({ ...proj, enabled: e.target.checked })} className="w-4 h-4 accent-[#D4A93A]" data-testid="project-enabled" />
                Projectpagina actief
              </label>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={saveProject} className="btn-light flex-1 justify-center" data-testid="project-save">OPSLAAN</button>
              <button onClick={() => setProj(null)} className="btn-ghost-dark text-[#A3A3A3] border-[#F5F3F0]/25" data-testid="project-cancel">ANNULEREN</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
